"use client";

import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import DirectoryCard from "./DirectoryCard";
import { getCurrentUser } from "@/lib/auth";

const GroupsMap = dynamic(() => import("./GroupsMap"), { ssr: false });

interface GroupEntry {
    id: number;
    name: string;
    type: string;
    status: string;
    region_code: string;
    description: string;
    url: string;
    volunteer_count?: number;
    active_count?: number;
    inactive_count?: number;
    geo_key?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    parent_id?: number | null;
    has_assignments?: boolean;
}

interface Props {
    groups: GroupEntry[];
    stats: { committees: number; chapters: number; communities: number };
    mapDisplayMode?: "assignments_only" | "all_active";
}

const REGION_META: Record<string, { label: string; color: string }> = {
    amer: { label: "North Americas (AMER)", color: "#003d45" },
    emea: { label: "Europe, Middle East & Africa (EMEA)", color: "#007a8a" },
    apac: { label: "Asia Pacific (APAC)", color: "#ae232d" },
    latam: { label: "Latin America (LATAM)", color: "#D69E2E" },
};

// ─── Hierarchy ordering ────────────────────────────────────────────────────────
// Maps entity name (lowercase keyword match) to its sort position.
// Entries not found fall to the end with alphabetical sub-sort.

type HierarchySection = {
    sectionLabel: string | null;    // null = no visible header (used for Board alone)
    sectionParent: string | null;   // for visual grouping
    sortKey: number;
    matchName: (name: string) => boolean;
};

const COMMITTEE_SECTIONS: HierarchySection[] = [
    // Board of Directors
    { sectionLabel: "Board of Directors", sectionParent: null, sortKey: 10, matchName: n => n.includes("board of directors") },
    // Committees of the Board
    { sectionLabel: "Committees of the Board", sectionParent: null, sortKey: 20, matchName: n => n.includes("audit committee") },
    { sectionLabel: "Committees of the Board", sectionParent: null, sortKey: 21, matchName: n => n.includes("executive committee") },
    { sectionLabel: "Committees of the Board", sectionParent: null, sortKey: 22, matchName: n => n.includes("finance committee") },
    { sectionLabel: "Committees of the Board", sectionParent: null, sortKey: 23, matchName: n => n.includes("governance committee") },
    // Committees of the Association — The CMT Experience
    { sectionLabel: "The CMT Experience", sectionParent: "Committees of the Association", sortKey: 30, matchName: n => n.includes("admission") },
    { sectionLabel: "The CMT Experience", sectionParent: "Committees of the Association", sortKey: 31, matchName: n => n.includes("curriculum") || n.includes("testing") },
    { sectionLabel: "The CMT Experience", sectionParent: "Committees of the Association", sortKey: 32, matchName: n => n.includes("ethics") },
    // Committees of the Association — Thought Leadership
    { sectionLabel: "Thought Leadership", sectionParent: "Committees of the Association", sortKey: 40, matchName: n => n.includes("dow award") || n.includes("charles h") },
    { sectionLabel: "Thought Leadership", sectionParent: "Committees of the Association", sortKey: 41, matchName: n => n.includes("journal") },
    // Committees of the Association — Outreach and Advocacy
    { sectionLabel: "Outreach and Advocacy", sectionParent: "Committees of the Association", sortKey: 50, matchName: n => n.includes("academ") },
    { sectionLabel: "Outreach and Advocacy", sectionParent: "Committees of the Association", sortKey: 51, matchName: n => n.includes("industry outreach") },
    // Committees of the Association — The Member Experience
    { sectionLabel: "The Member Experience", sectionParent: "Committees of the Association", sortKey: 60, matchName: n => n === "events committee" || (n.includes("event") && !n.includes("symposium") && !n.includes("summit")) },
    { sectionLabel: "The Member Experience", sectionParent: "Committees of the Association", sortKey: 60, matchName: n => n.includes("technology") && n.includes("intelligence") },
    { sectionLabel: "The Member Experience", sectionParent: "Committees of the Association", sortKey: 61, matchName: n => (n.includes("chapter development") || n.includes("cdc")) && !n.includes("amer") && !n.includes("apac") && !n.includes("emea") && !n.includes("latam") && !n.includes("north america") && !n.includes("asia-pacific") && !n.includes("europe") && !n.includes("latin america") },
    { sectionLabel: "Thematic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 62, matchName: n => n.includes("dual charter") },
    { sectionLabel: "Thematic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 63, matchName: n => n.includes("black caucus") },
    { sectionLabel: "Thematic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 64, matchName: n => n.includes("digital network") },
    { sectionLabel: "Thematic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 65, matchName: n => n.includes("women") },
    // Geographic Subcommittees
    { sectionLabel: "Geographic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 70, matchName: n => n.includes("amer") && n.includes("chapter") },
    { sectionLabel: "Geographic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 71, matchName: n => (n.includes("apac") || n.includes("asia-pacific") || n.includes("asia pacific")) && n.includes("chapter") },
    { sectionLabel: "Geographic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 72, matchName: n => (n.includes("emea") || n.includes("europe")) && n.includes("chapter") },
    { sectionLabel: "Geographic Subcommittees of the Chapter Development Committee", sectionParent: "Committees of the Association", sortKey: 73, matchName: n => (n.includes("latam") || n.includes("latin america")) && n.includes("chapter") },
];

function getCommitteeSortKey(name: string): number {
    const lower = name.toLowerCase();
    for (const section of COMMITTEE_SECTIONS) {
        if (section.matchName(lower)) return section.sortKey;
    }
    return 999; // unrecognised → end
}

function getCommitteeSectionLabel(name: string): string | null {
    const lower = name.toLowerCase();
    for (const section of COMMITTEE_SECTIONS) {
        if (section.matchName(lower)) return section.sectionLabel;
    }
    return null;
}

/**
 * Sort groups following the hierarchy. For 'all' tab: committees first (in
 * hierarchy order), then chapters (alphabetical), then communities (alphabetical).
 * For 'committee' tab: hierarchy order only.
 */
function sortByHierarchy(groups: GroupEntry[], tab: "all" | "committee" | "chapter" | "community"): GroupEntry[] {
    if (tab === "all") {
        const committees = groups.filter(g => g.type === "committee").sort((a, b) => {
            const ka = getCommitteeSortKey(a.name);
            const kb = getCommitteeSortKey(b.name);
            if (ka !== kb) return ka - kb;
            return a.name.localeCompare(b.name);
        });
        const byVolAsc = (a: GroupEntry, b: GroupEntry) => {
            const va = a.volunteer_count ?? a.active_count ?? 0;
            const vb = b.volunteer_count ?? b.active_count ?? 0;
            if (va !== vb) return vb - va;
            return a.name.localeCompare(b.name);
        };
        const chapters = groups.filter(g => g.type === "chapter").sort(byVolAsc);
        const communities = groups.filter(g => g.type === "community").sort(byVolAsc);
        return [...committees, ...chapters, ...communities];
    }
    if (tab === "committee") {
        return [...groups].sort((a, b) => {
            const ka = getCommitteeSortKey(a.name);
            const kb = getCommitteeSortKey(b.name);
            if (ka !== kb) return ka - kb;
            return a.name.localeCompare(b.name);
        });
    }
    // chapters / communities: descending volunteer count
    return [...groups].sort((a, b) => {
        const va = a.volunteer_count ?? a.active_count ?? 0;
        const vb = b.volunteer_count ?? b.active_count ?? 0;
        if (va !== vb) return vb - va;
        return a.name.localeCompare(b.name);
    });
}

// ─── Section header injection ──────────────────────────────────────────────────
type WithVisible = GroupEntry & { visible: boolean };

type RenderedItem =
    | { kind: "header"; label: string; depth: number; key: string }
    | { kind: "card"; group: WithVisible; editUrl?: string };

function injectSectionHeaders(
    groups: WithVisible[],
    tab: string,
    currentUser: any
): RenderedItem[] {
    const items: RenderedItem[] = [];
    // Only inject headers when viewing 'all' or 'committee' tab and no search running
    const injectHeaders = (tab === "all" || tab === "committee");

    let lastSection: string | null = "__UNSET__";
    let lastParent: string | null = "__UNSET__";

    groups.forEach(g => {
        if (g.visible && injectHeaders && g.type === "committee") {
            const section = getCommitteeSectionLabel(g.name);
            const sectionEntry = COMMITTEE_SECTIONS.find(s => s.matchName(g.name.toLowerCase()));
            const parent = sectionEntry?.sectionParent ?? null;

            // Inject parent header first
            if (parent !== lastParent) {
                if (parent) items.push({ kind: "header", label: parent, depth: 0, key: `hdr-parent-${parent}-${items.length}` });
                lastParent = parent;
                lastSection = "__UNSET__"; // force section header re-emit after parent
            }

            // Inject section header
            if (section !== lastSection) {
                if (section) items.push({ kind: "header", label: section, depth: 1, key: `hdr-section-${section}-${parent}-${items.length}` });
                lastSection = section;
            }
        } else if (g.visible && injectHeaders && g.type !== "committee") {
            // Group dividers for 'all' tab
            if (tab === "all") {
                const divLabel = g.type === "chapter" ? "Chapters" : "Communities";
                if (divLabel !== lastSection) {
                    items.push({ kind: "header", label: divLabel, depth: 0, key: `hdr-${divLabel}` });
                    lastSection = divLabel;
                    lastParent = null;
                }
            }
        }

        const editUrl = currentUser
            ? (currentUser.role === "admin" || currentUser.role === "superadmin") ||
              (currentUser.assignments || []).some((a: any) =>
                  String(a.entity_id) === String(g.id) && String(a.entity_type) === String(g.type)
              )
                ? `/dashboard/manage/groups/${g.type}/${g.id}/edit`
                : undefined
            : undefined;

        items.push({ kind: "card", group: g, editUrl });
    });
    return items;
}

export default function GroupsDirectoryClient({ groups, stats, mapDisplayMode = "assignments_only" }: Props) {
    const searchParams = useSearchParams();
    const initialRegion = (searchParams?.get("region") || "all").toLowerCase();
    const initialChapterId = searchParams?.get("chapter") ? parseInt(searchParams.get("chapter") as string) : null;
    const [activeType, setActiveType] = useState(initialRegion !== "all" ? "chapter" : "all");
    const [activeRegion, setActiveRegion] = useState(initialRegion);
    const [activeChapterId, setActiveChapterId] = useState<number | null>(initialChapterId);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [helpOpen, setHelpOpen] = useState(false);

    const activeChapter = useMemo(
        () => (activeChapterId ? groups.find(g => g.type === "chapter" && g.id === activeChapterId) ?? null : null),
        [groups, activeChapterId],
    );

    const mapGroups = useMemo(() => {
        if (mapDisplayMode === "all_active") return groups;
        return groups.filter(g => g.has_assignments);
    }, [groups, mapDisplayMode]);

    // Chapters reachable on the map: directly in mapGroups OR parent of a community in mapGroups
    const sidebarGroups = useMemo(() => {
        if (mapDisplayMode === "all_active") return mapGroups;
        const result = new Map<number, GroupEntry>();
        mapGroups.forEach(g => result.set(g.id, g));
        mapGroups.forEach(g => {
            if (g.type === "community" && g.parent_id) {
                const parent = groups.find(p => p.type === "chapter" && p.id === g.parent_id);
                if (parent && !result.has(parent.id)) result.set(parent.id, parent);
            }
        });
        return Array.from(result.values());
    }, [mapGroups, groups, mapDisplayMode]);

    const chaptersInRegion = useMemo(() => {
        if (activeRegion === "all") return [];
        return sidebarGroups
            .filter(g => g.type === "chapter" && (g.region_code || "").toLowerCase() === activeRegion)
            .sort((a, b) => {
                const va = a.volunteer_count ?? a.active_count ?? 0;
                const vb = b.volunteer_count ?? b.active_count ?? 0;
                return vb - va;
            });
    }, [sidebarGroups, activeRegion]);

    const communitiesInChapter = useMemo(() => {
        if (!activeChapter) return [];
        return sidebarGroups
            .filter(g => g.type === "community" && g.parent_id === activeChapter.id)
            .sort((a, b) => {
                const va = a.volunteer_count ?? a.active_count ?? 0;
                const vb = b.volunteer_count ?? b.active_count ?? 0;
                return vb - va;
            });
    }, [sidebarGroups, activeChapter]);

    const drillLevel: "world" | "region" | "chapter" =
        activeChapter ? "chapter" : (activeRegion !== "all" ? "region" : "world");

    useEffect(() => {
        getCurrentUser().then(user => {
            if (user) setCurrentUser(user);
        }).catch(err => console.error(err));
    }, []);

    const regionAggregates = useMemo(() => {
        const COUNTRY_TO_REGION: Record<string, string> = {
            US: "amer", CA: "amer",
            MX: "latam", BR: "latam", AR: "latam", CL: "latam", CO: "latam", PE: "latam",
        };
        const out: Record<string, { chapters: number; communities: number; volunteers: number }> = {
            amer: { chapters: 0, communities: 0, volunteers: 0 },
            emea: { chapters: 0, communities: 0, volunteers: 0 },
            apac: { chapters: 0, communities: 0, volunteers: 0 },
            latam: { chapters: 0, communities: 0, volunteers: 0 },
        };
        sidebarGroups.forEach((g) => {
            if (g.status && g.status.toLowerCase() !== "active") return;
            let r = (g.region_code || "").toLowerCase();
            if (!r && g.geo_key) {
                const cc = g.geo_key.split("-")[0];
                r = COUNTRY_TO_REGION[cc] || "";
            }
            if (!r || !out[r]) return;
            if (g.type === "chapter") out[r].chapters += 1;
            else if (g.type === "community") out[r].communities += 1;
            out[r].volunteers += g.active_count ?? g.volunteer_count ?? 0;
        });
        return out;
    }, [sidebarGroups]);

    const knownRegions = useMemo(() => {
        const kr: Record<string, number> = {};
        groups.forEach((g) => {
            if (g.region_code) {
                const rc = g.region_code.toLowerCase();
                kr[rc] = (kr[rc] || 0) + 1;
            }
        });
        return kr;
    }, [groups]);

    const chapterById = useMemo(() => {
        const m = new Map<number, GroupEntry>();
        groups.forEach(g => { if (g.type === "chapter") m.set(g.id, g); });
        return m;
    }, [groups]);

    const filtered = useMemo(() => {
        const q = searchTerm.toLowerCase();
        const baseFiltered: WithVisible[] = groups.map((g) => {
            const typeOk = activeType === "all" || g.type === activeType;
            // Resolve effective region: communities inherit from their parent chapter
            let effectiveRegion = (g.region_code || "").toLowerCase();
            if (!effectiveRegion && g.type === "community" && g.parent_id) {
                effectiveRegion = (chapterById.get(g.parent_id)?.region_code || "").toLowerCase();
            }
            const regionOk = activeRegion === "all" || effectiveRegion === activeRegion;
            // When drilled into a chapter, scope directory to that chapter + its communities
            const chapterScopeOk = !activeChapter
                || (g.type === "chapter" && g.id === activeChapter.id)
                || (g.type === "community" && g.parent_id === activeChapter.id)
                || g.type === "committee";
            const searchOk = !q || g.name.toLowerCase().includes(q);
            return { ...g, visible: typeOk && regionOk && chapterScopeOk && searchOk };
        });

        const tab = activeType as "all" | "committee" | "chapter" | "community";
        const sorted = sortByHierarchy(baseFiltered, tab) as WithVisible[];
        return sorted;
    }, [groups, activeType, activeRegion, activeChapter, chapterById, searchTerm]);

    // Tab clicks always reset the map drill — each tab is an independent flat filter.
    const handleTabClick = (type: string) => {
        setActiveType(type);
        setActiveRegion("all");
        setActiveChapterId(null);
    };

    // Map region click: zoom map + switch directory to Chapters filtered by region.
    const handleRegionClick = (region: string) => {
        setActiveRegion(region);
        setActiveChapterId(null);
        setActiveType(region !== "all" ? "chapter" : "all");
    };

    // Map chapter drill: zoom map + switch directory to Communities of that chapter.
    const handleChapterDrill = (chapter: GroupEntry) => {
        const hasCommunities = groups.some(g => g.type === "community" && g.parent_id === chapter.id);
        if (hasCommunities) {
            const r = (chapter.region_code || "").toLowerCase();
            if (r) setActiveRegion(r);
            setActiveChapterId(chapter.id);
            setActiveType("community");
        } else {
            window.location.href = chapter.url;
        }
    };

    const handleBackOneLevel = () => {
        if (activeChapter) {
            setActiveChapterId(null);
            setActiveType("chapter");
            // keep activeRegion so sidebar still shows chapters in this region
        } else if (activeRegion !== "all") {
            setActiveRegion("all");
            setActiveType("all");
        }
    };

    // Inject section headers into the rendered list (only when no search active)
    const renderedItems = useMemo(() => {
        const hasSearch = searchTerm.trim().length > 0;
        if (hasSearch) {
            // No headers when searching — just show cards
            return (filtered as WithVisible[]).map(g => {
                const editUrl = currentUser
                    ? (currentUser.role === "admin" || currentUser.role === "superadmin") ||
                      (currentUser.assignments || []).some((a: any) =>
                          String(a.entity_id) === String(g.id) && String(a.entity_type) === String(g.type)
                      )
                        ? `/dashboard/manage/groups/${g.type}/${g.id}/edit`
                        : undefined
                    : undefined;
                return { kind: "card" as const, group: g, editUrl };
            });
        }
        return injectSectionHeaders(filtered as WithVisible[], activeType, currentUser);
    }, [filtered, activeType, searchTerm, currentUser]);

    return (
        <>
            {/* Hero */}
            <header className="groups-hero">
                <h1>CMT Association Groups</h1>
                <p>
                    Explore our global network of Committees, Chapters, and Communities. Click a region on the map or use the tabs below to browse.{" "}
                    <button
                        type="button"
                        className="chapter-community-help-trigger"
                        onClick={() => setHelpOpen(v => !v)}
                        aria-expanded={helpOpen}
                        aria-label="Understanding Committees, Chapters and Communities"
                    >
                        <span aria-hidden="true">ⓘ</span> Understanding Committees, Chapters &amp; Communities
                    </button>
                </p>
                {helpOpen && (
                    <div className="chapter-community-help-popover" role="dialog" aria-label="Understanding Committees, Chapters and Communities">
                        <button
                            type="button"
                            className="chapter-community-help-close"
                            onClick={() => setHelpOpen(false)}
                            aria-label="Close"
                        >×</button>
                        <h3>Understanding Committees, Chapters &amp; Communities</h3>
                        <p className="popover-intro">All three engage CMT members, but differ in scale, cadence, and remit.</p>
                        <table>
                            <thead>
                                <tr><th>Criteria</th><th>Committee</th><th>Chapter</th><th>Community</th></tr>
                            </thead>
                            <tbody>
                                <tr><td>Geographic remit</td><td>Global</td><td>Regulatory jurisdiction (typically country)</td><td>35-mile radius with 10+ active members (typically city)</td></tr>
                                <tr><td>Event cadence</td><td>Not applicable</td><td>Annual (proposed 2028 onwards)</td><td>Quarterly</td></tr>
                                <tr><td>Member engagement</td><td>Global strategy for community and inclusion</td><td>Members outside formal communities, engaged digitally</td><td>Local meetups create a vibrant, collegial fraternity</td></tr>
                                <tr><td>Advocacy focus</td><td>Global engagement vision for each industry pillar</td><td>Direct engagement with domestic regulators and media</td><td>Direct engagement with local academia and employers</td></tr>
                                <tr><td>Min. eligibility to volunteer</td><td>Charter holder or professional member</td><td>Level 3 candidate</td><td>Level 2 candidate</td></tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </header>

            {/* Map */}
            <section id="orggroups-map" className="map-section">
                <div className="map-container-wrapper map-with-sidebar">
                    <div className="map-main">
                    <GroupsMap
                        groups={sidebarGroups}
                        activeRegion={activeRegion}
                        activeChapter={activeChapter}
                        onRegionChange={handleRegionClick}
                        onChapterDrill={handleChapterDrill}
                    />
                    </div>
                    <aside className="map-region-sidebar" aria-label="Drill-down summary">
                        <div className="sidebar-breadcrumb">
                            <button
                                type="button"
                                className={`crumb${drillLevel === "world" ? " current" : ""}`}
                                onClick={() => handleRegionClick("all")}
                            >World</button>
                            {activeRegion !== "all" && (
                                <>
                                    <span className="crumb-sep">›</span>
                                    <button
                                        type="button"
                                        className={`crumb${drillLevel === "region" ? " current" : ""}`}
                                        onClick={() => { setActiveChapterId(null); setActiveType("chapter"); }}
                                    >{activeRegion.toUpperCase()}</button>
                                </>
                            )}
                            {activeChapter && (
                                <>
                                    <span className="crumb-sep">›</span>
                                    <span className="crumb current">{activeChapter.name.replace(/\s+Chapter$/i, "")}</span>
                                </>
                            )}
                            {drillLevel !== "world" && (
                                <button type="button" className="crumb-back" onClick={handleBackOneLevel} aria-label="Back">↑</button>
                            )}
                        </div>

                        {drillLevel === "world" && (["amer","emea","apac","latam"] as const).map((code) => {
                            const meta = REGION_META[code];
                            const agg = regionAggregates[code];
                            if (!agg || agg.chapters + agg.communities === 0) return null;
                            return (
                                <button
                                    key={code}
                                    type="button"
                                    className="region-sidebar-card"
                                    onClick={() => handleRegionClick(code)}
                                    style={{ borderLeftColor: meta.color }}
                                >
                                    <div className="region-sidebar-type-badge" style={{ color: meta.color }}>Region</div>
                                    <div className="region-sidebar-label" style={{ color: meta.color }}>
                                        {meta.label}
                                    </div>
                                    <div className="region-sidebar-stats">
                                        <span><b>{agg.chapters}</b> chapter{agg.chapters !== 1 ? "s" : ""}</span>
                                        {agg.communities > 0 && <span><b>{agg.communities}</b> communit{agg.communities !== 1 ? "ies" : "y"}</span>}
                                    </div>
                                </button>
                            );
                        })}

                        {drillLevel === "region" && chaptersInRegion.map((ch) => {
                            const meta = REGION_META[activeRegion] || REGION_META.emea;
                            const commCount = groups.filter(g => g.type === "community" && g.parent_id === ch.id).length;
                            return (
                                <button
                                    key={ch.id}
                                    type="button"
                                    className="region-sidebar-card"
                                    onClick={() => handleChapterDrill(ch)}
                                    style={{ borderLeftColor: meta.color }}
                                >
                                    <div className="region-sidebar-type-badge" style={{ color: meta.color }}>Chapter</div>
                                    <div className="region-sidebar-label" style={{ color: meta.color }}>
                                        {ch.name.replace(/\s+Chapter$/i, "")}
                                    </div>
                                    <div className="region-sidebar-stats">
                                        <span><b>{commCount}</b> communit{commCount !== 1 ? "ies" : "y"}</span>
                                    </div>
                                </button>
                            );
                        })}

                        {drillLevel === "chapter" && (
                            communitiesInChapter.length > 0 ? communitiesInChapter.map((c) => {
                                const meta = REGION_META[activeRegion] || REGION_META.emea;
                                return (
                                    <a
                                        key={c.id}
                                        href={c.url}
                                        className="region-sidebar-card"
                                        style={{ borderLeftColor: meta.color, textDecoration: "none" }}
                                    >
                                        <div className="region-sidebar-type-badge" style={{ color: meta.color }}>Community</div>
                                        <div className="region-sidebar-label" style={{ color: meta.color }}>
                                            {c.name.replace(/\s+Community$/i, "")}
                                        </div>
                                    </a>
                                );
                            }) : (
                                <div style={{ fontSize: "0.78rem", color: "#666", padding: "0.5rem 0.25rem" }}>
                                    No communities in this chapter yet.{" "}
                                    {activeChapter && (
                                        <a href={activeChapter.url} style={{ color: "#007a8a", fontWeight: 600 }}>
                                            Visit chapter page →
                                        </a>
                                    )}
                                </div>
                            )
                        )}
                    </aside>
                    <div className="map-legend" aria-label="Map color legend">
                        <span className="map-legend-item">
                            <span className="map-legend-swatch" style={{ background: "#003d45", borderRadius: "50%" }} />
                            Community
                        </span>
                        <span className="map-legend-item">
                            <span className="map-legend-swatch" style={{ background: "#2d7d8a" }} />
                            Active chapter
                        </span>
                        <span className="map-legend-item">
                            <span className="map-legend-swatch" style={{ background: "#8ab0b5" }} />
                            Inactive / communities only
                        </span>
                        <span className="map-legend-item">
                            <span className="map-legend-swatch" style={{ background: "#EAE7E3", border: "1px solid #D5D0CC" }} />
                            No group
                        </span>
                        {mapDisplayMode === "assignments_only" && (
                            <span className="map-legend-item map-legend-note">
                                Showing groups with active assignments
                            </span>
                        )}
                    </div>
                </div>
            </section>

            {/* Tab + Directory */}
            <section className="directory-section">
                <div className="directory-inner">
                    <div className="filter-toolbar filter-toolbar-centered">
                        <div className="combined-filter-pills">
                            {[
                                { tab: "all", label: "All", count: stats.committees + stats.chapters + stats.communities, color: "#1a1a1a" },
                                { tab: "committee", label: "Committees", count: stats.committees, color: "#003d45" },
                                { tab: "chapter", label: "Chapters", count: stats.chapters, color: "#007a8a" },
                                { tab: "community", label: "Communities", count: stats.communities, color: "#ae232d" },
                            ].map(({ tab, label, count, color }) => (
                                <button
                                    key={tab}
                                    type="button"
                                    className={`combined-filter-pill${activeType === tab ? " active" : ""}`}
                                    onClick={() => handleTabClick(tab)}
                                    style={{ "--pill-color": color } as React.CSSProperties}
                                >
                                    <span className="combined-filter-pill-count">{count}</span>
                                    <span className="combined-filter-pill-label">{label}</span>
                                </button>
                            ))}
                        </div>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search groups..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div key={activeType} className="directory-grid-wrapper">
                        {renderedItems.every(item => item.kind === "card" && !item.group.visible) || renderedItems.length === 0 ? (
                            <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "3rem 1rem" }}>
                                <p style={{ color: "#666666" }}>No groups match the current filters.</p>
                            </div>
                        ) : (
                            renderedItems.map((item, idx) => {
                                if (item.kind === "header") {
                                    const isSub = item.depth > 0;
                                    return (
                                        <div
                                            key={item.key}
                                            className="directory-section-header"
                                            style={{
                                                gridColumn: "1 / -1",
                                                padding: isSub ? "0.6rem 1rem 0.4rem 2rem" : "0.75rem 1rem 0.4rem 1rem",
                                                borderLeft: isSub ? "3px solid #007a8a" : "4px solid #003d45",
                                                background: isSub ? "#f7faf9" : "#eef4f3",
                                                marginTop: idx === 0 ? 0 : "0.5rem",
                                            }}
                                        >
                                            <span style={{
                                                fontSize: isSub ? "0.68rem" : "0.72rem",
                                                fontWeight: 800,
                                                textTransform: "uppercase",
                                                letterSpacing: "0.15em",
                                                color: "#003d45",
                                            }}>
                                                {item.label}
                                            </span>
                                        </div>
                                    );
                                }

                                const g = item.group;
                                const commCount = g.type === "chapter"
                                    ? groups.filter(x => x.type === "community" && x.parent_id === g.id).length
                                    : 0;
                                return (
                                    <DirectoryCard
                                        key={`${g.type}-${g.id}`}
                                        id={g.id}
                                        name={g.name}
                                        type={g.type}
                                        status={g.status}
                                        regionCode={g.region_code}
                                        description={g.description}
                                        url={g.url}
                                        visible={g.visible}
                                        volunteerCount={g.volunteer_count}
                                        activeCount={g.active_count}
                                        inactiveCount={g.inactive_count}
                                        communityCount={commCount}
                                        editUrl={item.editUrl}
                                    />
                                );
                            })
                        )}
                    </div>
                </div>
            </section>

            <style jsx global>{`
                .filter-toolbar-centered {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 1rem;
                    margin: 0 auto 1.5rem;
                }
                .combined-filter-pills {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 0.75rem;
                }
                .combined-filter-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.55rem;
                    background: #fff;
                    border: 1.5px solid #d8e0df;
                    border-radius: 999px;
                    padding: 0.55rem 1.1rem;
                    cursor: pointer;
                    transition: all 0.18s ease;
                    font-family: inherit;
                }
                .combined-filter-pill:hover {
                    border-color: var(--pill-color);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                    transform: translateY(-1px);
                }
                .combined-filter-pill.active {
                    background: var(--pill-color);
                    border-color: var(--pill-color);
                }
                .combined-filter-pill.active .combined-filter-pill-count,
                .combined-filter-pill.active .combined-filter-pill-label {
                    color: #fff;
                }
                .combined-filter-pill-count {
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: var(--pill-color);
                    line-height: 1;
                }
                .combined-filter-pill-label {
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #1a1a1a;
                }
                .filter-toolbar-centered .search-input {
                    max-width: 360px;
                    width: 100%;
                }
                .directory-grid-wrapper {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 1rem;
                }
                .directory-section-header {
                    border-radius: 4px;
                }
                .chapter-community-help-trigger {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.3rem;
                    margin-left: 0.4rem;
                    padding: 0.2rem 0.6rem;
                    background: rgba(255,255,255,0.15);
                    border: 1px solid rgba(255,255,255,0.4);
                    color: inherit;
                    border-radius: 999px;
                    font-size: 0.78rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.15s;
                }
                .chapter-community-help-trigger:hover {
                    background: rgba(255,255,255,0.3);
                }
                .chapter-community-help-popover {
                    position: relative;
                    margin: 1rem auto 0;
                    max-width: 720px;
                    background: #fff;
                    color: #003d45;
                    border-radius: 12px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    padding: 1.25rem 1.5rem 1.5rem;
                    text-align: left;
                }
                .chapter-community-help-popover h3 {
                    margin: 0 0 0.35rem;
                    font-size: 1.05rem;
                    font-weight: 700;
                    color: #003d45;
                }
                .chapter-community-help-popover .popover-intro {
                    margin: 0 0 0.85rem;
                    font-size: 0.85rem;
                    color: #555;
                }
                .chapter-community-help-popover table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.82rem;
                }
                .chapter-community-help-popover th,
                .chapter-community-help-popover td {
                    border-bottom: 1px solid #eef0ef;
                    padding: 0.5rem 0.6rem;
                    vertical-align: top;
                    text-align: left;
                }
                .chapter-community-help-popover th {
                    background: #f7faf9;
                    font-weight: 700;
                    color: #007a8a;
                    text-transform: uppercase;
                    font-size: 0.7rem;
                    letter-spacing: 0.05em;
                }
                .chapter-community-help-popover td:first-child {
                    font-weight: 600;
                    color: #003d45;
                    width: 28%;
                }
                .chapter-community-help-close {
                    position: absolute;
                    top: 0.5rem;
                    right: 0.75rem;
                    background: transparent;
                    border: none;
                    font-size: 1.4rem;
                    line-height: 1;
                    color: #666;
                    cursor: pointer;
                }
                .chapter-community-help-close:hover { color: #003d45; }
                .map-container {
                    border-radius: 14px;
                    box-shadow: 0 6px 20px rgba(0, 61, 69, 0.08), inset 0 0 0 1px rgba(0, 61, 69, 0.04);
                    overflow: hidden;
                }
                .leaflet-container { background: #eaf2f4 !important; }
                .sidebar-breadcrumb {
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                    flex-wrap: wrap;
                    padding: 0.35rem 0.5rem;
                    font-size: 0.72rem;
                    color: #555;
                    margin-bottom: 0.25rem;
                    position: relative;
                }
                .sidebar-breadcrumb .crumb {
                    background: none;
                    border: none;
                    padding: 0.15rem 0.25rem;
                    cursor: pointer;
                    color: #007a8a;
                    font-weight: 600;
                    font-size: 0.72rem;
                    letter-spacing: 0.04em;
                }
                .sidebar-breadcrumb .crumb:hover:not(.current) { text-decoration: underline; }
                .sidebar-breadcrumb .crumb.current {
                    color: #003d45;
                    cursor: default;
                }
                .sidebar-breadcrumb .crumb-sep { color: #aaa; font-weight: 700; }
                .sidebar-breadcrumb .crumb-back {
                    margin-left: auto;
                    background: #fff;
                    border: 1px solid #cdd9d8;
                    color: #003d45;
                    width: 24px; height: 24px;
                    border-radius: 50%;
                    cursor: pointer;
                    font-weight: 700;
                    line-height: 1;
                }
                .sidebar-breadcrumb .crumb-back:hover { background: #eef4f3; }
                .map-with-sidebar {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) 220px;
                    gap: 1rem;
                    align-items: start;
                }
                .map-main { min-width: 0; }
                .map-region-sidebar {
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                    max-height: 520px;
                    overflow-y: auto;
                    padding-right: 4px;
                    scrollbar-width: thin;
                    scrollbar-color: #b6cccd #f0f4f3;
                }
                .map-region-sidebar::-webkit-scrollbar { width: 6px; }
                .map-region-sidebar::-webkit-scrollbar-track { background: #f0f4f3; border-radius: 4px; }
                .map-region-sidebar::-webkit-scrollbar-thumb { background: #b6cccd; border-radius: 4px; }
                .map-region-sidebar::-webkit-scrollbar-thumb:hover { background: #007a8a; }
                .sidebar-breadcrumb {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    background: linear-gradient(180deg, #eef2f1 85%, rgba(238,242,241,0));
                }
                .region-sidebar-card {
                    text-align: left;
                    background: #fff;
                    border: 1px solid #e2e8e7;
                    border-left: 4px solid #003d45;
                    border-radius: 8px;
                    padding: 0.6rem 0.75rem;
                    cursor: pointer;
                    transition: box-shadow 0.15s, transform 0.15s;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                }
                .region-sidebar-card:hover {
                    box-shadow: 0 4px 10px rgba(0,0,0,0.08);
                    transform: translateY(-1px);
                }
                .region-sidebar-card.active {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
                    background: #f7faf9;
                }
                .region-sidebar-type-badge {
                    font-size: 0.6rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.12em;
                    opacity: 0.7;
                    margin-bottom: 0.1rem;
                }
                .region-sidebar-label {
                    font-weight: 800;
                    font-size: 0.78rem;
                    letter-spacing: 0.08em;
                    margin-bottom: 0.3rem;
                }
                .region-sidebar-stats {
                    display: flex;
                    flex-direction: column;
                    gap: 0.15rem;
                    font-size: 0.75rem;
                    color: #555;
                }
                .region-sidebar-stats b { color: #003d45; }
                @media (max-width: 768px) {
                    .map-with-sidebar { grid-template-columns: 1fr; }
                    /* Clean 2-column card grid; breadcrumb spans full width on its own row */
                    .map-region-sidebar {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 0.6rem;
                        max-height: none;
                        overflow: visible;
                    }
                    .sidebar-breadcrumb { grid-column: 1 / -1; position: static; }
                    .region-sidebar-card { flex: none; }
                }
                @media (max-width: 420px) {
                    .map-region-sidebar { grid-template-columns: 1fr; }
                }
                .map-legend {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 1rem;
                    justify-content: center;
                    margin: 0.75rem 0 0.5rem;
                    font-size: 0.75rem;
                    color: #555;
                }
                .map-legend-item {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.4rem;
                }
                .map-legend-note {
                    color: #007a8a;
                    font-style: italic;
                    font-weight: 600;
                    font-size: 0.7rem;
                }
                .map-legend-swatch {
                    display: inline-block;
                    width: 14px;
                    height: 14px;
                    border-radius: 3px;
                }
                .leaflet-tooltip.map-tooltip {
                    background: rgba(15,23,42,0.95);
                    color: #fff;
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 10px;
                    padding: 10px 14px;
                    box-shadow: 0 6px 18px rgba(0,0,0,0.25);
                    font-family: Outfit, sans-serif;
                    font-size: 12.5px;
                    line-height: 1.45;
                }
                .leaflet-tooltip.map-tooltip:before { display: none; }
                .map-tooltip .mt-content b { font-size: 13.5px; }
                .map-tooltip .mt-row { margin-top: 4px; }
                .map-tooltip .mt-sub { opacity: 0.7; margin-top: 2px; font-size: 11.5px; }
                .map-tooltip .mt-hint { opacity: 0.5; margin-top: 6px; font-size: 11px; }
                .leaflet-container { font-family: inherit; background: transparent; }
                .leaflet-control-zoom {
                    border: none !important;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.08) !important;
                    border-radius: 8px !important;
                    overflow: hidden;
                }
                .leaflet-control-zoom a {
                    background: #ffffff !important;
                    color: #003d45 !important;
                    font-weight: 700 !important;
                }

                .map-tooltip .mt-dot {
                    display: inline-block;
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    margin-right: 6px;
                    vertical-align: middle;
                }
                .map-tooltip .mt-dot-chapter { background: #43d17a; }
                .map-tooltip .mt-dot-inactive { background: #aaa; }
                .map-tooltip .mt-inactive { opacity: 0.7; }
                .map-tooltip .mt-presence { color: #cde2e7; }

                .cmt-pin-icon { background: transparent !important; border: none !important; }
                .cmt-pin {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    pointer-events: auto;
                    filter: drop-shadow(0 3px 6px rgba(0,0,0,0.25));
                }
                .cmt-pin-head {
                    background: var(--pin-color);
                    color: #fff;
                    font-weight: 700;
                    font-size: 12px;
                    border: 2px solid #fff;
                    padding: 2px 8px;
                    min-width: 26px;
                    text-align: center;
                    border-radius: 14px;
                    line-height: 1.25;
                }
                .cmt-pin-stem {
                    width: 0;
                    height: 0;
                    border-left: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-top: 8px solid var(--pin-color);
                    margin-top: -1px;
                }

                .cmt-region-badge-icon { background: transparent !important; border: none !important; pointer-events: none; }
                .cmt-region-badge {
                    background: rgba(255,255,255,0.94);
                    color: #003d45;
                    border-radius: 10px;
                    padding: 6px 10px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
                    border-left: 4px solid var(--badge-color);
                    text-align: left;
                    font-size: 11px;
                    line-height: 1.3;
                    white-space: nowrap;
                }
                .cmt-region-badge-label {
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    font-size: 10px;
                    color: var(--badge-color);
                    margin-bottom: 2px;
                }
                .cmt-region-badge-stats { display: flex; gap: 10px; font-size: 11px; color: #555; }
                .cmt-region-badge-stats b { color: #003d45; }

            `}</style>
        </>
    );
}
